import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Col, Flex, Row, Typography, Form, Card, Button, Checkbox, message } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import dayjs from "dayjs";
import Layout from "../components/layout";
import Container from "../components/container";
import CurrentDoctor from "../../components/CurrentDoctor";
import Scheduler from "../../components/Scheduler";
import CreateAccountForm from "../../components/CreateAccount/createAccountForm";
import { REGISTER_AND_BOOK_APPOINTMENT } from "../components/doctors/oncologists.qql";
import Reviews from "../../components/Reviews";
import { DATE_FORMAT } from "../../constants/date";
import { appRoutes } from "../../constants/routes";
import { isSignedInState } from "../../store/slices/user";
import { useAppSelector } from "../../store";

const { Title, Text } = Typography;

const Book = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedData, setSelectedData] = useState<string | null>(null);
  const [tesseraSanitariaAuth, setTesseraSanitariaAuth] = useState<boolean>(false);
  const isSignedIn = useAppSelector(isSignedInState);

  const [
    registerAndBookAppointment,
     { loading: registerAndBookAppointmentLoading }] = useMutation(REGISTER_AND_BOOK_APPOINTMENT, {
      onCompleted: (data) => {
        if (data.registerAndBookAppointment.appointments[0].id) {
          navigate("/success", { state: { appointment: data.registerAndBookAppointment.appointments[0] } });
        } else {
          message.error("Something went wrong");
        }
      },
      onError: (error) => {
        message.error(error.graphQLErrors[0].message);
      }
    });

  if (!state?.doctor) {
    return <Navigate to="/" />;
  }

  if (isSignedIn) {
    return <Navigate to={appRoutes.appointments} />;
  }

  return (
    <Layout>
      <Container className="py-[1.5rem]">
        <Row gutter={20}>
          <Col span={24} xl={17}>
            <Flex vertical style={{ marginBottom: 24 }}>
              <Title level={3}>{t("appointmentBookingPage.title")}</Title>
              <Text type="secondary" style={{ marginBottom: 16 }}>
                {t("appointmentBookingPage.subtitle")}
              </Text>
              <Title level={3} style={{ margin: 0 }}>
                <span>
                  <s>€ 59</s> € 0
                </span>
              </Title>
            </Flex>
            <Form
              form={form}
              name="book"
              onFinish={(values) => {
                registerAndBookAppointment({
                  variables: {
                    ...values,
                    professionistId: +state.doctor.id,
                    startAt: selectedData,
                    tesseraSanitariaAuth,
                    phone: values.phone?.number ? values.phone.number : null,
                    birth_date: dayjs(values.birthDate).format(DATE_FORMAT.FULL_DATE),
                  }
                });
              }}
              layout="vertical"
            >
              <CreateAccountForm type="book" />
            </Form>
            <Title level={5} style={{ fontWeight: 400, marginBottom: 16, marginTop: 25 }}>
              Se non dovesse trovare la disponibilità oraria o giornaliera desiderata,
              La invitiamo a contattare direttamente il Suo psiconcologo.
              Può farlo comodamente scrivendo un messaggio nella sezione Messaggi qui
              sotto oppure inviando una mail a <a href="mailto:supporto@inbuonasalute.eu">supporto@inbuonasalute.eu</a>.
            </Title>
            <Card size="small">
              <Scheduler
                doctorData={{
                  id: state.doctor.id,
                  role: state.doctor.role
                }}
                onTimeClick={(e) => setSelectedData(`${e.current} ${e.from}:00`)}
              />
            </Card>
            <Row gutter={24} style={{ padding: "16px 0" }}>
              <Col span={24} sm={12}>
                <Checkbox
                  style={{ lineHeight: 1.2 }}
                  onChange={(e) => setTesseraSanitariaAuth(e.target.checked)}
                >
                  {t("appointmentBookingPage.checkbox")}
                </Checkbox>
              </Col>
              <Col span={24} sm={12}>
                <Flex justify="end">
                  <Button
                    type="primary"
                    size="large"
                    disabled={
                      registerAndBookAppointmentLoading
                      || !tesseraSanitariaAuth
                      || !selectedData
                    }
                    loading={registerAndBookAppointmentLoading}
                    onClick={() => form.submit()}
                  >
                    {t("appointmentBookingPage.submitButton")}
                  </Button>
                </Flex>
              </Col>
            </Row>
            <Reviews
              reviews={
                [...state.doctor.feedbacks, ...state.doctor.feedbacksManual.map((item: any) => {
              return {
                id: item.id,
                professionist: item.vote,
                comment: item.comment,
                updated_at: item.created_at,
                patient: {
                  name: item.patient_name,
                },
              };
            })]
}
            />
          </Col>
          <Col span={24} xl={7} style={{ paddingTop: 46 }}>
            <CurrentDoctor doctorData={state.doctor} />
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default Book;
